import {
  ActiveFilterState,
  FilterGroup,
  HeaderConfig,
  Row,
} from 'components/Table/KhulaSupplierTable';
import {IFarmInputInvoice} from 'constants/interfaces';
import moment from 'moment';
import {_NumberWithSpaces} from 'constants/index';
import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Select,
  Tooltip,
} from '@material-ui/core';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import {
  apiGetDepotOrder,
  apiUpdateInputOrderPaymentStatus,
  apiUpdateInputOrderStatus,
} from 'constants/Api';
import {reduxUpdateInvoice} from 'appredux/actions';

export const invoiceTableHeaders = (isAdmin: boolean): HeaderConfig[] => [
  {
    field: 'invoiceNumber',
    title: 'Invoice',
    isSortable: true,
    sortField: 'id',
    isSearchable: true,
    searchField: 'id',
    isRightAligned: true,
  },
  {
    field: 'formattedDate',
    title: 'Date',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  ...(isAdmin
    ? [
        {
          field: 'depotName',
          title: 'Depot',
          isSortable: true,
          isSearchable: true,
          isRightAligned: false,
        },
      ]
    : []),
  {
    field: 'farmerFullName',
    title: 'Farmer',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'formattedPaymentStatus',
    title: 'Payment Status',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'formattedDeliveryType',
    title: 'Delivery Type',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'invoiceStatusSelect',
    title: 'Invoice Status',
    isSortable: true,
    sortField: 'currOrderStatusDescription',
    isSearchable: true,
    searchField: 'currOrderStatusDescription',
    isRightAligned: false,
  },
  {
    field: 'formattedTotal',
    title: 'Total (R)',
    sortField: 'total',
    isSortable: true,
    isSearchable: false,
    isRightAligned: true,
    noWrap: true,
  },
  {
    field: 'actions',
    title: '',
    isSortable: false,
    isSearchable: false,
    isRightAligned: true,
  },
];

export const invoiceTableRows = (
  canWriteInvoices: boolean,
  canManageInvoicePayments: boolean,
  invoices: IFarmInputInvoice[],
  onSelect: (invoice: IFarmInputInvoice) => any,
): Row[] =>
  invoices.map((invoice) => {
    // Provide an explicit `id` for Row (string or number).
    // Adjust as needed if your Row type doesn't require it or uses a different key.
    const rowId = String(invoice.id);

    return {
      // The row ID for the table
      id: rowId,

      // Each column field your table needs:
      invoiceNumber: invoice.invoiceNumber,
      formattedDate: moment(parseInt(invoice.insertedDate, 10)).format(
        'YYYY-MM-DD',
      ),
      depotName: invoice.depot?.name || '',
      farmerFullName: invoice.farmer?.name + ' ' + invoice.farmer?.surname,
      currOrderStatusDescription: invoice.currOrderStatus.description,

      // We'll keep this as a raw string for searching/filtering:
      paymentStatus: invoice.isPaid ? 'Paid' : 'Unpaid',

      // A ReactNode for the table cell:
      formattedPaymentStatus: canManageInvoicePayments ? (
        <PaymentStatusSelect invoice={invoice} />
      ) : invoice.isPaid ? (
        'Paid'
      ) : (
        'Unpaid'
      ),

      formattedDeliveryType: getFormattedDeliveryType(invoice),

      // Another ReactNode column:
      invoiceStatusSelect: (
        <InvoiceStatusSelect invoice={invoice} disabled={!canWriteInvoices} />
      ),

      formattedTotal: 'R ' + _NumberWithSpaces(invoice.total),

      // The "actions" column with an icon:
      actions: <InvoiceRowIcons onSelect={() => onSelect(invoice)} />,
    };
  });

function getFormattedDeliveryType(invoice: IFarmInputInvoice) {
  const orderLogisticsTypes = invoice.orders.map(
    (order) => order.logisticsTypeName,
  );
  if (orderLogisticsTypes.includes('Supplier Custom')) return 'Custom';
  if (orderLogisticsTypes.includes('Courier Guy Delivery'))
    return 'Courier Guy Delivery';
  if (orderLogisticsTypes.includes('Free Delivery')) return 'Delivery';
  if (orderLogisticsTypes.includes('Pick Up')) return 'Collection';
  return invoice.deliveryType;
}

function InvoiceRowIcons(props: {onSelect: () => any}) {
  return (
    <Box display="flex" flexDirection="row">
      <Tooltip title="View invoice" aria-label="View invoice">
        <IconButton
          aria-label="view invoice"
          size="small"
          onClick={props.onSelect}
        >
          <ChatBubbleOutlineIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const PaymentStatusSelect = (props: {invoice: IFarmInputInvoice}) => {
  const [isPaid, setIsPaid] = React.useState(props.invoice.isPaid);
  const [tempIsPaid, setTempIsPaid] = React.useState(props.invoice.isPaid);
  const [open, setOpen] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<{value: unknown}>) => {
    setTempIsPaid(event.target.value === '1');
    setOpen(true);
  };

  const handleAccept = async () => {
    try {
      await apiUpdateInputOrderPaymentStatus(props.invoice.id, tempIsPaid);
      const updatedInvoice = await apiGetDepotOrder(props.invoice.id);
      reduxUpdateInvoice(updatedInvoice);
      setIsPaid(tempIsPaid);
      setOpen(false);
    } catch (error) {
      alert('Error updating invoice payment status');
      console.error(error);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <div>
      <Select native value={isPaid ? '1' : '0'} onChange={handleChange}>
        <option value="0">Unpaid</option>
        <option value="1">Paid</option>
      </Select>
      <Dialog
        className="invoice-confirmation-dialog"
        disableEscapeKeyDown
        open={open}
        onClose={handleCancel}
      >
        <DialogTitle>
          Update invoice payment status: {props.invoice.invoiceNumber}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to update the invoice payment status?
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="accept-button"
            onClick={handleAccept}
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const InvoiceStatusSelect = (props: {
  invoice: IFarmInputInvoice;
  disabled: boolean;
}) => {
  const {invoice, disabled} = props;
  const orderStatuses = invoice.orderStatus;
  const firstStatus =
    orderStatuses.find((item) => item.index === 0) || orderStatuses[0];
  const currentStatus =
    orderStatuses.find((item) => item.status === 'IN_PROGRESS') ||
    orderStatuses[0];

  const [orderStatus, setOrderStatus] = React.useState(
    currentStatus || firstStatus,
  );
  const [tempOrderStatus, setTempOrderStatus] = React.useState(orderStatus);
  const [open, setOpen] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const selectedIndex = parseInt(event.target.value as string, 10);
    const selectedStatus =
      orderStatuses.find((s) => s.index === selectedIndex) || orderStatus;
    setTempOrderStatus(selectedStatus);
    setOpen(true);
  };

  const handleAccept = async () => {
    const prevStatus = orderStatus;
    try {
      await apiUpdateInputOrderStatus(invoice.id, tempOrderStatus.index);
      const updatedInvoice = await apiGetDepotOrder(invoice.id);
      reduxUpdateInvoice(updatedInvoice);
      setOrderStatus(tempOrderStatus);
      setOpen(false);
    } catch (error) {
      alert('Error updating invoice status');
      console.error(error);
      setOrderStatus(prevStatus);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <div>
      <Select
        native
        value={orderStatus.index}
        onChange={handleChange}
        disabled={disabled}
      >
        {orderStatuses.map((status) => (
          <option key={status.index} value={status.index}>
            {status.description}
          </option>
        ))}
      </Select>
      <Dialog
        className="invoice-confirmation-dialog"
        disableEscapeKeyDown
        open={open}
        onClose={handleCancel}
      >
        <DialogTitle>
          Update invoice status: {invoice.invoiceNumber}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to update the invoice status?
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="accept-button"
            onClick={handleAccept}
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

/**
 * Filter groups
 */
const deliveryTypeFilterGroup: FilterGroup = {
  name: 'Delivery Type',
  filters: [
    {field: 'formattedDeliveryType', value: 'Supplier Custom', title: 'Custom'},
    {
      field: 'formattedDeliveryType',
      value: 'Courier Guy Delivery',
      title: 'Courier Guy Delivery',
    },
    {field: 'formattedDeliveryType', value: 'Collection', title: 'Collection'},
    {field: 'formattedDeliveryType', value: 'Delivery', title: 'Delivery'},
  ],
};

const paymentStatusFilterGroup: FilterGroup = {
  name: 'Payment Status',
  filters: [
    {field: 'paymentStatus', value: 'Paid', title: 'Paid'},
    {field: 'paymentStatus', value: 'Unpaid', title: 'Unpaid'},
  ],
};

export const invoiceTableFilters = [
  deliveryTypeFilterGroup,
  paymentStatusFilterGroup,
];

export const getDefaultPaidFilter = (isPaid: boolean): ActiveFilterState => {
  return {
    filterGroupId: paymentStatusFilterGroup.name,
    filterIndex: isPaid ? 0 : 1,
  };
};
